import { Type } from '@angular/core';
import { ValidatorFn } from '@angular/forms';
import {
  BaseDataSourceAction,
  BaseDataSourceWithOptions,
  FormConfig,
  OptionsModel,
} from '@d-components/form/form.interface';
import { BaseResponseModel } from '@d-models';
import { TemplateUrl } from '@d-models/services/product-name.model';
import { Observable } from 'rxjs';

interface IPosition {
  top?: string;
  left?: string;
  right?: string;
  bottom?: string;
}

export interface TableConfig<T> {
  dataKey?: string;
  selectable?: boolean;
  inBulkOption?: {
    label: string;
    icon?: string;
    command?: () => void;
  }[];
  inBulkable?:
    | {
        position: IPosition;
      }
    | false;
  exportable?:
    | {
        position: IPosition;
      }
    | false;
  importable?:
    | {
        position: IPosition;
        importFunc: (file: File) => Observable<any>;
        submitFunc: (...args: any) => Observable<any>;
        getTemplateFunc: (
          ...args: any
        ) => Observable<BaseResponseModel<TemplateUrl>>;
        title: string;
        templateName: string;
        submitSuccessMessage: string;
        dataArea?: {
          startRow?: number;
          startCol?: number;
          newFileName?: string;
          sheetIndex?: number;
        };
        downloadMessage: string;
        overrideFlag?: boolean;
      }
    | false;
  columnsConfig: ColumnConfig<T>[];
  isRowSelectable?: (rowData: T) => boolean;
  toolbarConfig?: TableToolConfig<T>;
  externalData?: any;
  topButtons?: {
    buttons: {
      label: string;
      icon?: string;
      outlined?: boolean;
      onClick?: (e: MouseEvent) => void;
      actionPermission?: {
        actionType: string | string[];
        moduleCode?: string;
        createdBy?: string;
      };
    }[];
    position: IPosition;
  };
}

export enum COLUMN_TYPE {
  Index = 'index',
  DataCode = 'dataCode',
  Status = 'status',
  MoreAction = 'moreAction',
  DataListWithCount = 'dataListWithCount',
  DataWithCountNo = 'dataWithCountNo',
  DateFormat = 'dateFormat',
  TextWithSub = 'textWithSub',
  ListActionIcon = 'listActionIcon',
  CheckBox = 'checkbox',
  CustomCell = 'customCell',
  Textbox = 'textbox',
  Dropdown = 'dropdown',
  InputNumber = 'inputNumber',
  Percentage = 'percentage',
  Number = 'number',
  ToggleButton = 'toggleButton',
}

export interface TableToolConfig<T> {
  display: boolean;
  actionList?: {
    splitButtonItems: {
      label: string;
      icon?: string;
      command?: (items?: T[]) => void;
    }[];
    actionType?: string[];
  };

  multipleDelete?: {
    confirmPopup?: Type<any>;
    onDelete: (items: T[], reason?: string) => void;
    confirmPopupParams?: any;
  };

  multipleDownload?: {
    onDownload: (items: T[]) => void;
  };
}

export type ColumnConfig<T> =
  | MoreActionColumnConfig<T>
  | DateFormatColumnConfig
  | TextWithSubColumnConfig
  | ListActionIconColumnConfig<T>
  | StatusColumnConfig
  | CustomCellConfig
  | CheckboxColumnConfig<T>
  | TextboxColumnConfig
  | DropdownColumnConfig
  | DataListWithCountColumnConfig
  | InputNumberColumnConfig
  | DataCodeColumnConfig
  | BaseColumnConfig<T>
  | DataWithCountNoColumnConfig
  | NumberColumnConfig;

export interface DataListWithCountColumnConfig extends BaseColumnConfig<any> {
  type: COLUMN_TYPE.DataListWithCount;
  displayField: string;
  separatorCharacter?: string;
}

export interface NumberColumnConfig extends BaseColumnConfig<any> {
  type: COLUMN_TYPE.Number;
  format?: string;
}

export interface DataWithCountNoColumnConfig extends BaseColumnConfig<any> {
  type: COLUMN_TYPE.DataWithCountNo;
  countNoField: string;
}

interface BaseColumnConfig<T> {
  columnName?: string;
  dataField: string;
  headerCurrency?: string;
  type?: COLUMN_TYPE;
  sortable?: boolean;
  sortField?: string | string[];
  cssContainer?: string;
  cssClassWrapper?: (e: MouseEvent, rowData: T) => string;
  cssTitle?: string;
  colSpan?: number;
  editConfig?: PartialBy<FormConfig, 'layoutDefine'>;
  searchable?: boolean;
  onClick?: (e: MouseEvent, rowData: T) => void;
  actionPermission?: {
    actionName: string | string[];
    moduleCode?: string;
  };
  toolTip?: (rowData: T) => string;
  hidden?: boolean;
  disabled?: boolean | ((rowData: T) => boolean);
}

interface CheckboxColumnConfig<T> extends BaseControlColumnConfig<T> {
  type: COLUMN_TYPE.CheckBox;
  options?: { label: any; value: any }[];
}

export type DropdownColumnConfig = BaseControlColumnConfig<any> &
  Omit<BaseDataSourceAction, 'options'> & {
    type: COLUMN_TYPE.Dropdown;
    options: OptionsModel[];
    onSelected?: any;
  };

interface InputNumberColumnConfig extends BaseControlColumnConfig<any> {
  type: COLUMN_TYPE.InputNumber;
  min?: number;
  max?: number;
  minFractionDigits?: number;
  maxFractionDigits?: number;
  maxLength?: number;
}

interface TextboxColumnConfig extends BaseControlColumnConfig<any> {
  type: COLUMN_TYPE.Textbox;
}

interface BaseControlColumnConfig<T> extends BaseColumnConfig<T> {
  type:
    | COLUMN_TYPE.CheckBox
    | COLUMN_TYPE.Dropdown
    | COLUMN_TYPE.Textbox
    | COLUMN_TYPE.InputNumber;
  validators?: ValidatorFn[];
  errorMessageList: { [key: string]: string };
  actionPermission?: {
    actionName: string | string[];
  };
}

type PartialBy<T, K extends keyof T> = Omit<T, K> & Partial<Pick<T, K>>;
interface IMenuItemsColumn<T> {
  label?: string;
  disabled?: boolean;
  getDisabled?: (rowData: T) => boolean;
  onClick: (rowData: T, rowIndex: number) => void;
}

export interface MoreActionColumnConfig<T>
  extends PartialBy<BaseColumnConfig<T>, 'dataField'> {
  type: COLUMN_TYPE.MoreAction;
  menuItems: IMenuItemsColumn<T>[];
  sortable?: false;
}

export interface StatusColumnConfig extends BaseColumnConfig<any> {
  statusOptions?: OptionsModel[];
  type: COLUMN_TYPE.Status;
}

interface DataCodeColumnConfig extends BaseColumnConfig<any> {
  options: OptionsModel[];
  type: COLUMN_TYPE.DataCode;
}

export interface CustomCellConfig
  extends PartialBy<BaseColumnConfig<any>, 'dataField'> {
  type: COLUMN_TYPE.CustomCell;
  cellName: string;
}

export interface ListActionIconColumnConfig<T>
  extends PartialBy<BaseColumnConfig<T>, 'dataField'> {
  type: COLUMN_TYPE.ListActionIcon;
  actionList: {
    icon: string | ((rowData: T, indexRow: number) => string);
    toolTip?: string;
    onClick: (rowData: T, rowIndex: number) => void;
    isHasPermission?: (rowData: T, indexRow: number) => boolean;
    onHide?: (rowData: T, rowIndex: number) => boolean;
    actionPermission?: {
      actionName: string | string[];
      moduleCode?: string | string[] | object;
    };
  }[];
  sortable?: false;
  hidden?: boolean;
}

export interface DateFormatColumnConfig extends BaseColumnConfig<any> {
  type: COLUMN_TYPE.DateFormat;
  format?: string;
}

export interface TextWithSubColumnConfig extends BaseColumnConfig<any> {
  type: COLUMN_TYPE.TextWithSub;
  subDataField?: string;
  textWithSubOnclick?: (e: MouseEvent, rowData: any) => void;
  subDataConfig?: {
    isDateTime?: boolean;
    format?: string;
  };
  subColumnName?: string;
  subColumnType?: COLUMN_TYPE;
  subStatusOption?: OptionsModel[];
  cssSubField?: string;
}

export const isDateFormatColumnConfig = (
  item: ColumnConfig<any>,
): item is DateFormatColumnConfig => {
  return item.type === COLUMN_TYPE.DateFormat;
};

export const isTextWithSubColumnConfig = (
  item: ColumnConfig<any>,
): item is TextWithSubColumnConfig => {
  return item.type === COLUMN_TYPE.TextWithSub;
};

export const isDataWithCountColumnConfig = (
  item: ColumnConfig<any>,
): item is DataListWithCountColumnConfig => {
  return item.type === COLUMN_TYPE.DataListWithCount;
};

export const isDataWithCountFieldColumnConfig = (
  item: ColumnConfig<any>,
): item is DataWithCountNoColumnConfig => {
  return item.type === COLUMN_TYPE.DataWithCountNo;
};

export const isStatusColumnConfig = (
  item: ColumnConfig<any>,
): item is StatusColumnConfig => {
  return item.type === COLUMN_TYPE.Status;
};

export const isDataCodeColumnConfig = (
  item: ColumnConfig<any>,
): item is DataCodeColumnConfig => {
  return item.type === COLUMN_TYPE.DataCode;
};

export type TableViewMode = 'active' | 'all' | 'approve';

export type TableChangeEvent = {
  sortBy: {
    fieldName: string;
    ascending: boolean;
  }[];
  first: number;
  pageIndex: number;
  rowPerPage: number;
  sortField: string;
  sortOrder: string;
  typeChange: 'PageChange' | 'PageSizeChange' | 'SortChange' | 'Other';
};
